import DeleteIcon from "@mui/icons-material/Delete";
import * as React from "react";

import { AlertDialog, RobotoButton } from "@/shared/components";
import { useDeleteTrigger } from "@/shared/domain/actions/hooks";
import { TriggerView } from "@/shared/domain/actions/TriggerRecord";
import { OrgRecord } from "@/shared/domain/orgs";
import { ErrorMonitoringService } from "@/shared/services";

interface DeleteTriggerButtonProps {
  trigger: TriggerView;
  onDelete: (trigger: TriggerView) => void;
  org: OrgRecord;
  setErrorText: (arg: string) => void;
}

/**
 * Deletes a trigger after confirmation.
 */
export default function DeleteTriggerButton({
  trigger,
  onDelete,
  org,
  setErrorText,
}: DeleteTriggerButtonProps) {
  const [alertDialogOpen, setAlertDialogOpen] = React.useState(false);

  const deleteTriggerMutation = useDeleteTrigger();

  const dialogAction = React.useCallback(async () => {
    setErrorText("");
    try {
      await deleteTriggerMutation.mutateAsync({
        name: trigger.name,
        resourceOwnerId: org.org_id,
        triggerId: trigger.trigger_id,
        triggerType: trigger.trigger_type,
      });
      onDelete(trigger);
      setAlertDialogOpen(false); // close dialog on success
    } catch (caughtError) {
      const err =
        caughtError instanceof Error
          ? caughtError
          : new Error(String(caughtError));
      ErrorMonitoringService.captureError(err, { trigger });
      setErrorText(err.message);
    }
  }, [trigger, org.org_id, onDelete, setErrorText, deleteTriggerMutation]);

  return (
    <>
      <RobotoButton
        color="error"
        eventName="DeleteTriggerClicked"
        eventProperties={{ triggerId: trigger.trigger_id }}
        onClick={() => setAlertDialogOpen(true)}
        startIcon={<DeleteIcon />}
        variant="outlined"
        size="small"
        disabled={deleteTriggerMutation.isPending} // reflect loading state
      >
        Delete
      </RobotoButton>

      <AlertDialog
        dialogOpen={alertDialogOpen}
        handleClose={() => setAlertDialogOpen(false)}
        dialogTitle="Delete Trigger"
        dialogText={`Are you sure you want to delete ${trigger.name}?`}
        dialogAction={dialogAction}
      />
    </>
  );
}
